import type { Event, Stacktrace } from "@sentry/types";

// Drops an open upstream mapbox-gl-js bug (#13590) where a mouseup on the map
// canvas reaches `vec4.transformMat4` with an undefined `pixelMatrixInverse`
// via `unproject` → `pointRayIntersection`. Stacktrace is entirely inside
// node_modules/mapbox-gl and there is no first-party mitigation.
// https://github.com/mapbox/mapbox-gl-js/issues/13590
const MAPBOX_UNPROJECT_FUNCTIONS = [
  "transformMat4",
  "pointRayIntersection",
  "pointLocation3D",
  "pointCoordinate3D",
];

const isMapboxGlFrame = (filename: string | undefined, absPath: string | undefined): boolean =>
  !!filename?.includes("mapbox-gl") || !!absPath?.includes("mapbox-gl");

const isMapboxUnprojectFunction = (functionName: string | undefined): boolean => {
  if (!functionName) return false;

  return MAPBOX_UNPROJECT_FUNCTIONS.some(name => functionName.includes(name));
};

const stacktraceIsMapboxUnprojectCrash = (stacktrace?: Stacktrace): boolean => {
  const frames = stacktrace?.frames;
  if (!frames?.length) return false;

  return frames.some(
    frame =>
      isMapboxGlFrame(frame.filename, frame.abs_path) && isMapboxUnprojectFunction(frame.function)
  );
};

const errorIsMapboxGlUnprojectCrash = (event: Event): boolean => {
  return !!event?.exception?.values?.some(exception =>
    stacktraceIsMapboxUnprojectCrash(exception?.stacktrace)
  );
};

const mapboxGlUnproject = (event: Event): Event | null => {
  if (errorIsMapboxGlUnprojectCrash(event)) {
    return null;
  }

  return event;
};

export { mapboxGlUnproject };
